import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articleEntries } from "@/mocks/articles";

export default function Navbar() {
  const [catOpen, setCatOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) setGuideOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const closeAll = () => {
    setCatOpen(false);
    setGuideOpen(false);
    setMobileOpen(false);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="flex items-center justify-between h-16 md:h-18 px-4 md:px-6">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" onClick={closeAll}>
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-950 whitespace-nowrap">
              Sugargoo <span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className="text-sm font-semibold text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              Home
            </Link>

            <div ref={catRef} className="relative">
              <button
                onClick={() => { setCatOpen(!catOpen); setGuideOpen(false); }}
                className="flex items-center gap-1 text-sm font-semibold text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${catOpen ? "rotate-180" : ""}`}></i>
              </button>
              {catOpen && (
                <div className="absolute top-full mt-2 left-0 bg-background-50 rounded-xl border border-background-200/70 shadow-sm min-w-[200px] py-2 z-50 max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      onClick={closeAll}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div ref={guideRef} className="relative">
              <button
                onClick={() => { setGuideOpen(!guideOpen); setCatOpen(false); }}
                className="flex items-center gap-1 text-sm font-semibold text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${guideOpen ? "rotate-180" : ""}`}></i>
              </button>
              {guideOpen && (
                <div className="absolute top-full mt-2 right-0 bg-background-50 rounded-xl border border-background-200/70 shadow-sm min-w-[300px] py-2 z-50 max-h-[420px] overflow-y-auto">
                  {articleEntries.map((entry, idx) => (
                    <Link
                      key={idx}
                      to={entry.slug}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer"
                      onClick={closeAll}
                    >
                      <span className="block font-medium truncate max-w-[270px]">{entry.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              sugargoo spreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-5 py-2 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl text-foreground-950 ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={closeAll}
          />
          <div className="absolute top-0 right-0 bottom-0 w-[85vw] max-w-[360px] bg-background-50 shadow-lg overflow-y-auto">
            <div className="flex items-center justify-between px-4 h-16 border-b border-background-200/70">
              <span className="font-heading text-base font-bold text-foreground-950">
                Menu
              </span>
              <button
                className="w-10 h-10 flex items-center justify-center cursor-pointer"
                onClick={closeAll}
                aria-label="Close menu"
              >
                <i className="ri-close-line text-xl text-foreground-950"></i>
              </button>
            </div>

            <div className="px-4 py-4 flex flex-col gap-4">
              <Link
                to="/"
                className="flex items-center gap-3 px-3 py-3 text-base font-semibold text-foreground-950 hover:bg-primary-50 rounded-xl transition-colors cursor-pointer"
                onClick={closeAll}
              >
                <div className="w-9 h-9 flex items-center justify-center bg-primary-100 rounded-lg">
                  <i className="ri-home-4-line text-lg text-primary-500"></i>
                </div>
                Home
              </Link>

              <div className="pt-1">
                <p className="px-3 mb-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                  Categories
                </p>
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-1.5 px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      onClick={closeAll}
                    >
                      <i className="ri-arrow-right-up-line text-xs text-foreground-400 flex-shrink-0"></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>

              <div className="pt-1 border-t border-background-200/70">
                <p className="px-3 mb-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                  Shopping Guides
                </p>
                <div className="flex flex-col gap-0.5">
                  {articleEntries.map((entry, idx) => (
                    <Link
                      key={idx}
                      to={entry.slug}
                      className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-600 rounded-lg transition-colors cursor-pointer"
                      onClick={closeAll}
                    >
                      <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-accent-100 rounded-md">
                        <i className={`${entry.icon} text-sm text-accent-600`}></i>
                      </div>
                      <span className="line-clamp-2 leading-snug">{entry.title}</span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="pt-2 border-t border-background-200/70 flex flex-col gap-3">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-700 hover:text-primary-500 rounded-lg transition-colors cursor-pointer"
                  onClick={closeAll}
                >
                  <div className="w-9 h-9 flex items-center justify-center bg-primary-100 rounded-lg">
                    <i className="ri-external-link-line text-lg text-primary-500"></i>
                  </div>
                  sugargoo spreadsheet
                </a>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 px-5 py-3.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  onClick={closeAll}
                >
                  <i className="ri-shopping-bag-line"></i>
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}